import { useCallback, useMemo, useRef } from "react";

/** @typedef {{ skipFirstColumn?: boolean; skipLastColumn?: boolean }} ComplianceRowNavigationSkipOptions */

export const isInteractiveComplianceTableRowTarget = (target) =>
  target?.closest?.(".ch-checkbox") ||
  target?.closest?.(".interactive-element") ||
  target?.closest?.(".link-btn") ||
  target?.closest?.(".dropdown-menu") ||
  target?.closest?.(".dropdown-item") ||
  target?.closest?.(".actions-status-box");

/**
 * Skip row navigation for non-navigable table columns (e.g. checkbox, actions).
 * @param {import("react").MouseEvent | { target?: EventTarget | null; currentTarget?: EventTarget | null }} event
 * @param {ComplianceRowNavigationSkipOptions} [options]
 */
export const shouldSkipComplianceRowNavigation = (
  event,
  options = {},
) => {
  const { skipFirstColumn = false, skipLastColumn = true } = options;

  if (isInteractiveComplianceTableRowTarget(event.target)) return true;

  const clickedCell = event.target?.closest?.("td");
  const rowEl = event.currentTarget;
  if (!clickedCell || !rowEl) return false;

  const cells = rowEl.querySelectorAll("td");
  if (!cells.length) return false;

  const cellIndex = Array.from(cells).indexOf(clickedCell);
  if (skipFirstColumn && cellIndex === 0) return true;
  if (skipLastColumn && cellIndex === cells.length - 1) return true;
  return false;
};

/** Folder listing tables: skip checkbox (first) and actions (last) columns. */
export const shouldSkipComplianceFolderRowNavigation = (event) =>
  shouldSkipComplianceRowNavigation(event, {
    skipFirstColumn: true,
    skipLastColumn: true,
  });

/**
 * Row click navigation for compliance-style policy tables.
 * Skips navigation when the user interacts with checkboxes, links, or action menus.
 * @param {Function} onRowClick
 * @param {ComplianceRowNavigationSkipOptions} [rowNavigationSkipOptions]
 */
export function useComplianceTableRowClick(
  onRowClick,
  rowNavigationSkipOptions = { skipLastColumn: true },
) {
  const suppressRowNavigationRef = useRef(false);

  const preventRowNavigation = useCallback((e) => {
    e?.stopPropagation?.();
    suppressRowNavigationRef.current = true;
    window.setTimeout(() => {
      suppressRowNavigationRef.current = false;
    }, 300);
  }, []);

  const runDropdownAction = useCallback(
    (e, action) => {
      preventRowNavigation(e);
      action?.();
    },
    [preventRowNavigation],
  );

  const rowEvents = useMemo(
    () => ({
      onClick: (e, row) => {
        if (suppressRowNavigationRef.current) return;
        if (shouldSkipComplianceRowNavigation(e, rowNavigationSkipOptions)) {
          return;
        }
        onRowClick?.(row);
      },
    }),
    [onRowClick, rowNavigationSkipOptions],
  );

  return { preventRowNavigation, runDropdownAction, rowEvents };
}
